import {
  Music,
  Coins,
  MoreHorizontal,
  CheckCircle,
  Star,
  Trophy,
  MessageCircle,
  ThumbsDown,
  ThumbsUp,
  Share2Icon,
  Share,
  ChevronUp,
} from 'lucide-react';
import { Menu, MenuButton, MenuItems, MenuItem } from '@headlessui/react';
import { useInView } from 'react-intersection-observer';
import { useCallback, useEffect, useMemo, useState } from 'react';
import Lottie from 'lottie-react';
import celebrationAnimation from '../Celebration.json';
import GiftAnimation from '../Gift-Chance.json';
import { API_ENDPOINTS } from '../utils/constants';
import { fetchWithAuth } from './authService';
import { useNavigate } from 'react-router-dom';
import CommentsModal from './CommentModal';
import { PiShareFat } from 'react-icons/pi';

const globalStyles = `
@keyframes pulseSoft {
  0% { box-shadow: 0 0 0 0 grey; }
  70% { box-shadow: 0 0 0 12px rgba(219, 15, 17, 0); }
  100% { box-shadow: 0 0 0 0 rgba(219, 15, 17, 0); }
}
@keyframes ambientFloat {
  0% { opacity: 0; transform: translate3d(0, 20px, 0) scale(0.92) rotate(0deg); }
  12% { opacity: 0.5; }
  70% { opacity: 0.35; }
  100% { opacity: 0; transform: translate3d(0, -110vh, 0) scale(1.06) rotate(8deg); }
}
@keyframes bounceOnce {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-8px); }
}
`;
if (
  typeof document !== 'undefined' &&
  !document.getElementById('chance-animations')
) {
  const style = document.createElement('style');
  style.id = 'chance-animations';
  style.innerHTML = globalStyles;
  document.head.appendChild(style);
}

// ✅ QuestionCard Component
function QuestionCard({ question, onAnswer, showToast, removeQuestion }) {
  const [selected, setSelected] = useState(null);
  const [isRevealed, setIsRevealed] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  const [resultMsg, setResultMsg] = useState('');
  const [selectedReaction, setSelectedReaction] = useState(
    question.userReaction || null
  );
  const [openComments, setOpenComments] = useState(false);
  const [rewardCoins, setRewardCoins] = useState(1);
  const [isWin, setIsWin] = useState(false);

  const audio = new Audio('/sounds/coin_sound.mp3');

  const winMessages = useMemo(
    () => [
      `Champion! 🏆 +${rewardCoins} coins`,
      `You nailed it! ⭐ +${rewardCoins} coins`,
      `Perfect choice! 🎯 +${rewardCoins} coins`,
      `In sync with the crowd! 🔥 +${rewardCoins} coins`,
    ],
    [rewardCoins] // ✅ re-compute when rewardCoins updates
  );

  const loseMessages = useMemo(
    () => [
      'Better luck next time 🎧',
      'Close one! Try again 💪',
      'The crowd went another way 🤔',
    ],
    []
  );

  const majorityIdx = question.stats?.choicePercentages
    ? question.stats.choicePercentages.indexOf(
        Math.max(...question.stats.choicePercentages)
      )
    : -1;

  const handleSelect = async idx => {
    setSelected(idx);

    try {
      // ✅ Call Answer API
      const res = await fetchWithAuth(API_ENDPOINTS.CHANCE_ANSWER, {
        method: 'POST',
        body: JSON.stringify({
          questionId: question.questionId,
          selectedChoice: idx,
        }),
      });

      if (!res.ok) throw new Error('Failed to submit answer');
      const data = await res.json();

      // ✅ Update question with new stats
      question.stats = data.stats;
      setIsRevealed(true);
      setRewardCoins(data.coinsRewarded || 1);

      // ✅ Refresh profile (coins)
      if (onAnswer) {
        onAnswer();
      }

      // ✅ Majority index from updated stats
      const newMajorityIdx = data?.stats?.choicePercentages
        ? data.stats.choicePercentages.indexOf(
            Math.max(...data.stats.choicePercentages)
          )
        : -1;

      if (idx === newMajorityIdx) {
        setIsWin(true); // ✅ won
        setResultMsg(
          winMessages[Math.floor(Math.random() * winMessages.length)]
        );
        setShowCelebration(true);
        audio.play();
        setTimeout(() => setShowCelebration(false), 2500);
      } else {
        setIsWin(false); // ❌ lost
        setResultMsg(
          loseMessages[Math.floor(Math.random() * loseMessages.length)]
        );
      }
    } catch (err) {
      console.error('Failed to submit answer:', err);
      showToast(err.message || 'Error submitting your answer');
    }
  };

  const Post_Reaction = useCallback(async (reactionType, questionId) => {
    try {
      const res = await fetchWithAuth(API_ENDPOINTS.CHANCE_REACTION, {
        method: 'POST',
        body: JSON.stringify({
          questionId,
          reactionType,
        }),
      });

      if (!res.ok) throw new Error('Failed to post reaction');
      const data = await res.json();
      // console.log('Reaction posted:', data);
      // showToast('Saved your choice, thanks! ❤️');
      return data;
    } catch (err) {
      console.error('Error posting reaction:', err);
      showToast(err.message || 'Error saving your choice');
    }
  }, []);

  const Remove_Reaction = useCallback(async questionId => {
    try {
      const res = await fetchWithAuth(API_ENDPOINTS.CHANCE_REMOVE_REACTION, {
        method: 'POST',
        body: JSON.stringify({ questionId }),
      });

      if (!res.ok) throw new Error('Failed to remove reaction');
      return await res.json();
    } catch (err) {
      console.error('Error removing reaction:', err);
      showToast(err.message || 'Error removing your choice');
    }
  }, []);

  const handleAction = useCallback(
    async (actionType, questionId) => {
      try {
        const res = await fetchWithAuth(API_ENDPOINTS.CHANCE_ACTION, {
          method: 'POST',
          body: JSON.stringify({
            questionId,
            actionType, // "report" or "not_interested"
          }),
        });

        if (!res.ok) throw new Error('Failed to save action');

        showToast('Saved your choice, thanks! ❤️');

        if (removeQuestion) removeQuestion(questionId);

        return await res.json();
      } catch (err) {
        console.error('Error posting action:', err);
        showToast(err.message || 'Error saving your choice');
      }
    },
    [removeQuestion, showToast]
  );

  const handleReaction = async type => {
    const prevReaction = selectedReaction;

    // clone stats so we can update
    const newStats = { ...question.reactionStats };

    if (selectedReaction === type) {
      // remove reaction
      setSelectedReaction(null);

      // optimistically adjust counts
      if (type === 'like') newStats.totalLikes -= 1;
      if (type === 'dislike') newStats.totalDislikes -= 1;
      question.reactionStats = newStats;

      try {
        await Remove_Reaction(question.questionId);
      } catch (err) {
        console.error('Error removing reaction:', err);
        setSelectedReaction(prevReaction); // rollback
        // rollback counts
        if (type === 'like') newStats.totalLikes += 1;
        if (type === 'dislike') newStats.totalDislikes += 1;
        question.reactionStats = newStats;
        showToast(err.message || 'Error removing your choice');
      }
    } else {
      // switching or adding new reaction
      setSelectedReaction(type);

      // optimistically adjust counts
      if (prevReaction === 'like') newStats.totalLikes -= 1;
      if (prevReaction === 'dislike') newStats.totalDislikes -= 1;
      if (type === 'like') newStats.totalLikes += 1;
      if (type === 'dislike') newStats.totalDislikes += 1;
      question.reactionStats = newStats;

      try {
        await Post_Reaction(type, question.questionId);
      } catch (err) {
        console.error('Error posting reaction:', err);
        setSelectedReaction(prevReaction); // rollback
        // rollback counts
        question.reactionStats = { ...question.reactionStats, ...prevReaction };
        showToast(err.message || 'Error updating reaction');
      }
    }
  };

  const formatCount = num => {
    if (num >= 1000) {
      return (num / 1000).toFixed(1).replace(/\.0$/, '') + 'k';
    }
    return num;
  };

  return (
    <article
      style={{
        background: 'white',
        borderRadius: '0.75rem',
        boxShadow:
          '0 4px 6px -1px rgba(0, 0, 0, 0.10), 0 2px 4px -1px rgba(0, 0, 0, 0.06), 0 20px 25px -5px rgba(0, 0, 0, 0.10), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
        padding: '1rem',
        marginBottom: '0.6rem',
        position: 'relative',
        transition: 'transform 0.3s cubic-bezier(0.4,0,0.2,1)',
      }}
    >
      {showCelebration && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 3000,
            pointerEvents: 'none',
          }}
        >
          <Lottie animationData={celebrationAnimation} loop={false} autoplay />
        </div>
      )}

      {/* Question */}
      <header
        style={{
          marginBottom: '1rem',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: '0.25rem',
        }}
      >
        <h3
          style={{
            fontWeight: 'bold',
            fontSize: '1.025rem',
            color: '#1f2937',
            textAlign: 'left',
          }}
        >
          {question.question}
        </h3>
        {/* Menu */}
        <Menu>
          <MenuButton
            style={{
              borderRadius: '9999px',
              border: 'none',
              background: 'transparent',
              color: '#000',
              WebkitTextFillColor: '#000',
              textDecoration: 'none',
              WebkitTapHighlightColor: 'transparent',
              WebkitFocusRingColor: 'transparent',
            }}
          >
            <MoreHorizontal
              style={{
                height: '1.25rem',
                width: '1.25rem',
                marginBottom: '5px',
                WebkitTapHighlightColor: 'transparent',
                WebkitFocusRingColor: 'transparent',
              }}
            />
          </MenuButton>

          <MenuItems
            style={{
              position: 'absolute',
              right: '1rem',
              zIndex: 50,
              width: '10rem',
              borderRadius: '0.375rem',
              background: 'white',
              padding: '0.25rem',
              fontSize: '0.875rem',
              boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
              color: '#000',
              WebkitTextFillColor: '#000',
              textDecoration: 'none',
              WebkitTapHighlightColor: 'transparent',
              WebkitFocusRingColor: 'transparent',
            }}
          >
            <MenuItem>
              <button
                style={{
                  display: 'flex',
                  width: '100%',
                  alignItems: 'center',
                  gap: '0.5rem',
                  borderRadius: '0.375rem',
                  padding: '0.5rem 0.75rem',
                  transition: 'background 0.2s',
                  color: '#000',
                  WebkitTextFillColor: '#000',
                  textDecoration: 'none',
                  border: 'none',
                  outline: 'none',
                  background: 'transparent',
                }}
                onClick={() =>
                  handleAction('not_interested', question.questionId)
                }
              >
                Not Interested
              </button>
            </MenuItem>

            <MenuItem>
              <button
                style={{
                  display: 'flex',
                  width: '100%',
                  alignItems: 'center',
                  gap: '0.5rem',
                  borderRadius: '0.375rem',
                  padding: '0.5rem 0.75rem',
                  transition: 'background 0.2s',
                  color: '#000',
                  WebkitTextFillColor: '#000',
                  textDecoration: 'none',
                  border: 'none',
                  outline: 'none',
                  background: 'transparent',
                }}
                onClick={() => handleAction('report', question.questionId)}
              >
                Wrong Answer
              </button>
            </MenuItem>
            <MenuItem>
              <button
                style={{
                  display: 'flex',
                  width: '100%',
                  alignItems: 'center',
                  gap: '0.5rem',
                  borderRadius: '0.375rem',
                  padding: '0.5rem 0.75rem',
                  transition: 'background 0.2s',
                  color: '#000',
                  WebkitTextFillColor: '#000',
                  textDecoration: 'none',
                  border: 'none',
                  outline: 'none',
                  background: 'transparent',
                }}
                onClick={() => handleReaction('dislike')}
              >
                Dislike
              </button>
            </MenuItem>
          </MenuItems>
        </Menu>
      </header>

      {/* Answer Choices */}
      {!isRevealed && (
        <div style={{ marginTop: '1rem', display: 'grid', gap: '0.75rem' }}>
          {question.choices.map((choice, idx) => (
            <button
              key={idx}
              onClick={() => handleSelect(idx)}
              style={{
                width: '100%',
                borderRadius: '0.5rem',
                border: '1px solid #d1d5db',
                background: '#f1f5f9',
                padding: '0.75rem 1rem',
                textAlign: 'left',
                boxShadow:
                  '0 4px 6px -1px rgba(0, 0, 0, 0.10), 0 2px 4px -1px rgba(0, 0, 0, 0.06), 0 20px 25px -5px rgba(0, 0, 0, 0.10), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                transition: 'transform 0.2s ease',
              }}
            >
              <span
                style={{
                  fontWeight: 500,
                  color: '#000', // force black
                  WebkitTextFillColor: '#000', // iOS Safari fix
                  textDecoration: 'none',
                  fontSize: '0.9rem',
                }}
              >
                {choice}
              </span>
            </button>
          ))}
        </div>
      )}

      {/* Results */}
      {isRevealed && (
        <div
          style={{ marginTop: '1rem', display: 'grid', gap: '0.75rem' }}
          aria-label="Results"
        >
          {question.choices.map((choice, idx) => {
            const picked = selected === idx;
            const isMajority = idx === majorityIdx;
            const bothSame = picked && isMajority;

            const percentage = Math.round(
              question.stats.choicePercentages?.[idx] || 0
            );

            // ✅ Background color logic
            let baseBg = '#f9fafb'; // default light grey
            if (isMajority) baseBg = '#ecfdf5'; // light green

            // ✅ Border color logic
            let borderClr = '#d1d5db'; // default grey
            if (isMajority) borderClr = '#16a34a'; // green border

            // ✅ Progress bar color
            let progressClr = '#9ca3af'; // default grey
            if (isMajority) progressClr = '#16a34a'; // green

            return (
              <div
                key={idx}
                style={{
                  position: 'relative',
                  borderRadius: '0.5rem',
                  border: `1px solid ${borderClr}`,
                  padding: '0.75rem',
                  background: baseBg,
                  opacity: isMajority ? 1 : 0.6, // dim only "other" options
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    fontSize: '0.9rem',
                    gap: '1rem',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      gap: '0.5rem',
                      textAlign: 'left',
                      flex: 1,
                    }}
                  >
                    <span>{choice}</span>

                    {/* Both picked & majority */}
                    {bothSame && (
                      <span style={{ display: 'inline-flex', gap: '0.25rem' }}>
                        <CheckCircle
                          style={{
                            height: '1rem',
                            width: '1rem',
                            color: '#DB0F11',
                          }}
                        />
                        <Star
                          style={{
                            height: '1rem',
                            width: '1rem',
                            color: '#16a34a',
                          }}
                        />
                      </span>
                    )}

                    {/* Your Selection */}
                    {picked && !bothSame && (
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '0.25rem',
                        }}
                      >
                        <CheckCircle
                          style={{
                            height: '1rem',
                            width: '1rem',
                            color: '#DB0F11',
                          }}
                        />
                        {choice.length <= 18 ? (
                          <span
                            style={{
                              fontSize: '0.75rem',
                              fontWeight: 500,
                              color: '#DB0F11',
                            }}
                          >
                            Your Selection
                          </span>
                        ) : choice.length <= 25 ? (
                          <span
                            style={{
                              fontSize: '0.75rem',
                              fontWeight: 500,
                              color: '#DB0F11',
                            }}
                          >
                            Yours
                          </span>
                        ) : null}
                      </span>
                    )}

                    {/* Majority */}
                    {isMajority && !bothSame && (
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '0.25rem',
                          color: '#16a34a',
                        }}
                      >
                        <Star style={{ height: '1rem', width: '1rem' }} />
                        {choice.length <= 25 && (
                          <span
                            style={{ fontSize: '0.75rem', fontWeight: 500 }}
                          >
                            Majority
                          </span>
                        )}
                      </span>
                    )}
                  </div>

                  <span style={{ fontWeight: '500', fontSize: '1rem' }}>
                    {percentage}%
                  </span>
                </div>

                {/* ✅ ProgressBar with dynamic colors */}
                <div
                  style={{
                    marginTop: '0.5rem',
                    height: '0.5rem',
                    width: '100%',
                    borderRadius: '9999px',
                    backgroundColor: '#e5e7eb',
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      height: '100%',
                      borderRadius: '9999px',
                      width: `${percentage}%`,
                      transition: 'width 0.5s ease-in-out',
                      backgroundColor: progressClr,
                    }}
                  />
                </div>
              </div>
            );
          })}
          {/* Result Message */}
          <div
            style={{
              marginTop: '0.75rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.25rem',
              borderRadius: '0.5rem',
              padding: '1rem 0.75rem',
              border: isWin ? '1px solid #bfdbfe' : '1px solid #f1f5f9',
              background: isWin ? '#dcfce7' : '#f1f5f9',
              color: isWin ? '#16a34a' : '#374151',
              fontWeight: 500,
              animation: isWin ? 'bounceOnce 0.9s ease infinite' : 'none',
            }}
          >
            <Trophy
              style={{
                height: '1.25rem',
                width: '1.25rem',
                color: isWin ? '#16a34a' : '#9ca3af',
              }}
            />
            <span style={{ fontSize: '1rem' }}>{resultMsg}</span>
          </div>
        </div>
      )}

      {/* ✅ Reaction Counts Row (same width as options) */}
      {(question.reactionStats?.totalLikes > 0 ||
        question.reactionStats?.totalDislikes > 0 ||
        question.reactionStats?.comments > 0) && (
        <div
          style={{
            marginTop: '1rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '0.75rem 0 0 0',
            width: '100%',
            maxWidth: '100%',
          }}
        >
          {/* Left Side → Like + Dislike */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '1rem',
              padding: '0 0.5rem',
            }}
          >
            {question.reactionStats?.totalLikes > 0 && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.35rem',
                }}
              >
                <div
                  style={{
                    padding: '4px',
                    borderRadius: '50%',
                    backgroundColor: '#DB0F11',
                    border: '1px solid black',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '21px',
                    height: '21px',
                  }}
                >
                  <ThumbsUp
                    style={{
                      height: '0.7rem',
                      width: '0.7rem',
                      color: 'white',
                      fill: 'white',
                    }}
                  />
                </div>

                <span
                  style={{
                    fontSize: '0.8rem',
                    color: '#374151',
                    fontWeight: '600',
                  }}
                >
                  {formatCount(question.reactionStats.totalLikes)}{' '}
                  {question.reactionStats.totalLikes === 1 ? 'Like' : 'Likes'}
                </span>
              </div>
            )}

            {question.reactionStats?.totalDislikes > 0 && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.35rem',
                }}
              >
                <div
                  style={{
                    padding: '4px',
                    borderRadius: '50%',
                    backgroundColor: '#DB0F11',
                    border: '1px solid black',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '21px',
                    height: '21px',
                  }}
                >
                  <ThumbsDown
                    style={{
                      height: '0.9rem',
                      width: '0.9rem',
                      color: 'white',
                      fill: 'white',
                    }}
                  />
                </div>

                <span
                  style={{
                    fontSize: '0.8rem',
                    color: '#374151',
                    fontWeight: '600',
                  }}
                >
                  {formatCount(question.reactionStats.totalDislikes)}{' '}
                  {question.reactionStats.totalDislikes === 1
                    ? 'Dislike'
                    : 'Dislikes'}
                </span>
              </div>
            )}
          </div>

          {/* Right Side → Comments */}
          {question.reactionStats?.comments > 0 && (
            <div
              style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}
            >
              <span
                style={{
                  fontSize: '0.8rem',
                  color: '#374151',
                  fontWeight: '600',
                }}
              >
                {formatCount(question.reactionStats.comments)}{' '}
                {question.reactionStats.comments === 1 ? 'Comment' : 'Comments'}
              </span>
            </div>
          )}
        </div>
      )}

      <footer
        style={{
          marginTop: '1rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingTop: '0.75rem',
          flexWrap: 'nowrap', // ✅ force single line
        }}
      >
        <button
          onClick={() => handleReaction('like')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.3rem',
            color: selectedReaction === 'like' ? '#DB0F11' : '#6b7280',
            fontWeight: 500,
            border: 'none',
            background: 'transparent',
            cursor: 'pointer',
            fontSize: 'clamp(0.9rem, 2vw, 1.2rem)', // ✅ responsive font size
            whiteSpace: 'nowrap', // ✅ prevent wrapping
          }}
        >
          <ThumbsUp
            fill={selectedReaction === 'like' ? '#DB0F11' : 'none'}
            stroke={selectedReaction === 'like' ? '#DB0F11' : '#6b7280'}
            style={{ height: '1.3rem', width: '1.3rem' }}
          />
          Like
        </button>

        <button
          onClick={() => setOpenComments(true)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.3rem',
            color: '#6b7280',
            fontWeight: 500,
            border: 'none',
            background: 'transparent',
            cursor: 'pointer',
            fontSize: 'clamp(0.9rem, 2vw, 1.2rem)',
            whiteSpace: 'nowrap',
          }}
        >
          <MessageCircle
            style={{ height: '1.3rem', width: '1.3rem' }}
            stroke="#6b7280"
          />
          Comment
        </button>

        <button
          onClick={() => {
            const shareMessage = `Everyone’s already answering on Tringbox Chance

Which Bollywood DJ anthem deserves a comeback?

Don’t miss out, match the majority now and grab coins

Redeem them for exciting coupons or play songs 🎁

Play before it’s too late 👉 https://user.tringbox.com/launch/chance/question/${question.questionId}`;

            if (navigator.share) {
              navigator
                .share({
                  title: 'Tringbox Chance',
                  text: shareMessage, // ✅ full message with link
                })
                .catch(err => console.log('Share failed:', err));
            } else {
              navigator.clipboard.writeText(shareMessage);
              showToast('Copied message to clipboard!');
            }
          }}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.3rem',
            color: '#6b7280',
            fontWeight: 500,
            border: 'none',
            background: 'transparent',
            cursor: 'pointer',
            fontSize: 'clamp(0.9rem, 2vw, 1.2rem)',
            whiteSpace: 'nowrap',
          }}
        >
          <PiShareFat
            fill="#6b7280"
            stroke="#6b7280"
            style={{ height: '1.3rem', width: '1.3rem' }}
          />
          Share
        </button>

        <CommentsModal
          open={openComments}
          onClose={() => setOpenComments(false)}
          questionId={question.questionId}
          showToast={showToast}
          onCommentAdded={() => {
            question.reactionStats.comments =
              (question.reactionStats.comments ?? 0) + 1;
          }}
        />
      </footer>
    </article>
  );
}

// ✅ TagFilter Component
function TagFilter({ tags, value = [], onChange }) {
  const toggleTag = tag => {
    if (tag === 'All') {
      // ✅ Clear all if "All" clicked
      onChange([]);
    } else {
      if (value.includes(tag)) {
        // ✅ remove tag
        onChange(value.filter(t => t !== tag));
      } else {
        // ✅ add tag
        onChange([...value, tag]);
      }
    }
  };

  const isActive = tag =>
    value.length === 0 && tag === 'All' ? true : value.includes(tag);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: '0.5rem',
        overflowX: 'auto',
        whiteSpace: 'nowrap',
        padding: '1rem 0',
        width: '100%',
        scrollbarWidth: 'none',
      }}
    >
      {tags.map(t => (
        <button
          key={t}
          onClick={() => toggleTag(t)}
          style={{
            flex: '0 0 auto',
            borderRadius: '0.5rem',
            border: 'none',
            padding: '0.375rem 1rem',
            fontSize: '0.8rem',
            fontWeight: 500,
            background: isActive(t)
              ? 'linear-gradient(90deg, #DB0F11 0%, #863663 100%)'
              : 'white',
            color: isActive(t) ? 'white' : '#d7080c',
            whiteSpace: 'nowrap',
            boxShadow: '0px 0px 4px 0px rgba(0, 0, 0, 0.25)',
            transition: 'transform 0.2s ease',
          }}
        >
          {t}
        </button>
      ))}
    </div>
  );
}

// ✅ Main Component
export default function ChancePage({ setActive }) {
  const [tagsSelected, setTagsSelected] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [isEnd, setIsEnd] = useState(false);
  const { ref: sentinelRef, inView } = useInView({ rootMargin: '400px' });
  const [profile, setprofile] = useState({});
  const [tags, setTags] = useState([]);
  const [isToast, setIsToast] = useState(false);
  const [ToastMsg, setToastMsg] = useState('');
  const [showScrollToTop, setShowScrollToTop] = useState(false);
  const [scrollContainer, setScrollContainer] = useState(null);
  const [showPoster, setShowPoster] = useState(false);
  const [posterUrl, setPosterUrl] = useState(null);
  const animatedTexts = [
    'Pick with the majority ✨',
    'Win coins. Redeem rewards',
  ];
  const [currentTextIndex, setCurrentTextIndex] = useState(0);
  const navigate = useNavigate();
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTextIndex(prev => (prev + 1) % animatedTexts.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  // Scroll to top button visibility - using container scroll instead of window
  useEffect(() => {
    const handleScroll = () => {
      if (scrollContainer) {
        const scrollTop = scrollContainer.scrollTop;
        setShowScrollToTop(scrollTop > 300);
      }
    };

    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll, {
        passive: true,
      });
      return () => scrollContainer.removeEventListener('scroll', handleScroll);
    }
  }, [scrollContainer]);

  const scrollToTop = () => {
    if (scrollContainer) {
      scrollContainer.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
  };

  const fetchProfile = useCallback(async () => {
    try {
      const response = await fetchWithAuth(API_ENDPOINTS.USERS, {
        method: 'GET',
      });

      if (!response.ok) throw new Error('Failed to fetch data');
      const data = await response.json();
      setprofile(data);
    } catch (error) {
      console.error('Error fetching profile:', error);
    }
  }, []);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  // First-time poster logic
  useEffect(() => {
    try {
      const hasSeen = localStorage.getItem('chancePosterSeen');
      const url = localStorage.getItem('ChancePoster');
      setPosterUrl(url);
      if (!hasSeen && url) {
        setShowPoster(true);
      }
    } catch (e) {
      // ignore localStorage errors
    }
  }, []);

  const fetchTags = useCallback(async () => {
    try {
      const res = await fetchWithAuth(API_ENDPOINTS.CHANCE_TAGS, {
        method: 'GET',
      });
      if (!res.ok) throw new Error('Failed to fetch tags');
      const data = await res.json();

      // ✅ Ensure "All" is always first
      setTags(data);
    } catch (err) {
      console.error('Error fetching tags:', err);
      // fallback to just All
      setTags(['All']);
    }
  }, []);

  useEffect(() => {
    fetchTags();
  }, [fetchTags]);

  const fetchQuestions = useCallback(async (pageNum, selectedTags) => {
    setIsLoading(true);
    try {
      // if none selected → fetch all
      const tagQuery =
        selectedTags.length > 0
          ? `&tags=${encodeURIComponent(selectedTags.join(','))}`
          : '';

      const res = await fetchWithAuth(
        `${API_ENDPOINTS.CHANCE_QUESTIONS}?limit=10&page=${pageNum}${tagQuery}&random=true&excludeAnswered=true`
      );

      const data = await res.json();

      if (!data.questions || data.questions.length === 0) {
        setIsEnd(true);
      } else {
        setQuestions(prev =>
          pageNum === 1 ? data.questions : [...prev, ...data.questions]
        );
      }
    } catch (err) {
      console.error('Failed to fetch questions:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    setQuestions([]);
    setIsEnd(false);
    setPage(1);
    fetchQuestions(1, tagsSelected);
  }, [tagsSelected, fetchQuestions]);

  useEffect(() => {
    if (inView && !isLoading && !isEnd) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchQuestions(nextPage, tagsSelected);
    }
  }, [inView, isLoading, isEnd, page, tagsSelected, fetchQuestions]);

  const showToast = msg => {
    setToastMsg(msg);
    setIsToast(true);
    setTimeout(() => setIsToast(false), 2500); // auto-hide
  };
  const removeQuestion = questionId => {
    setQuestions(prev => prev.filter(q => q.questionId !== questionId));
  };

  return (
    <div
      style={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#f1f5f9',
      }}
    >
      {showPoster && posterUrl && (
        <div
          onClick={() => {
            setShowPoster(false);
            try {
              localStorage.setItem('chancePosterSeen', 'true');
            } catch (e) {
              // ignore localStorage errors
            }
          }}
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.6)',
            zIndex: 20000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '1rem',
          }}
        >
          <img
            src={posterUrl}
            alt="Chance Poster"
            style={{
              maxWidth: '80vw',
              maxHeight: '70vh',
              borderRadius: '12px',
              boxShadow: '0 10px 30px rgba(0,0,0,0.4)',
            }}
          />
        </div>
      )}
      {isToast && (
        <div
          style={{
            position: 'fixed',
            bottom: '90px',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 10000,
            background: 'white',
            borderRadius: '20px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.32)',
            padding: '16px 20px',
            minWidth: '300px',
            maxWidth: '90vw',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
          }}
        >
          <img
            src="/images/logo.svg"
            style={{ width: '18px', height: '18px' }}
          />
          <div style={{ color: 'black', fontSize: '13px' }}>{ToastMsg}</div>
        </div>
      )}
      <header
        style={{
          flexShrink: 0,
          borderBottom: '1px solid #e5e7eb',
          background: 'white',
          padding: '0 1rem',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-end',
              gap: '0.35rem',
              padding: '3px 0 3px 0',
            }}
          >
            <Lottie
              animationData={GiftAnimation}
              loop={true}
              autoplay
              style={{ height: '55px', width: '55px', marginBottom: '5px' }}
            />

            <div>
              <h1
                style={{
                  fontSize: '1.25rem',
                  fontWeight: 'bold',
                  lineHeight: '1.1',
                  textAlign: 'left',
                  margin: 0,
                }}
              >
                Chance
              </h1>
              <p
                style={{
                  fontSize: '0.875rem',
                  color: '#4b5563',
                  margin: 0,
                }}
              >
                <span
                  style={{
                    position: 'relative',
                    display: 'inline-block',
                    overflow: 'hidden',
                  }}
                >
                  {animatedTexts[currentTextIndex]}
                  <span
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: '-100%',
                      width: '100%',
                      height: '100%',
                      background:
                        'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.6) 50%, transparent 100%)',
                      animation: 'shimmer 3s infinite',
                    }}
                  />
                </span>
              </p>
            </div>
          </div>

          <button
            onClick={() => navigate('/buy')}
            className="text-end py-0"
            style={{
              position: 'relative',
              border: '1.321px solid #FED111',
              borderRadius: '20px ',
              color: 'black',
              textDecoration: 'none',
              outline: 'none',
              background: 'transparent',
              padding: 0,
              marginTop: '10px',
            }}
          >
            <span
              className="text-end d-flex flex-row align-items-center"
              style={{
                color: 'black',
                borderColor: 'white',
                gap: '5px',
                paddingRight: '12px',
                paddingLeft: '0px',
              }}
            >
              <img
                src="/images/coin.svg"
                className=""
                style={{
                  height: '30px',
                }}
              />
              {profile.coins || 0}
            </span>
          </button>
        </div>
      </header>

      <section
        ref={setScrollContainer}
        style={{
          flex: 1,
          overflowY: 'auto',
          margin: '0 auto',
          width: '100%',
          maxWidth: '36rem',
          padding: '0 1rem',
          paddingBottom: '2rem',
        }}
      >
        <TagFilter
          tags={tags}
          value={tagsSelected}
          onChange={setTagsSelected}
        />
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '1rem',
          }}
        >
          {questions.map(q => (
            <QuestionCard
              key={q.questionId}
              question={q}
              onAnswer={fetchProfile}
              showToast={showToast}
              removeQuestion={removeQuestion}
            />
          ))}
        </div>
        <div ref={sentinelRef} style={{ height: '2.5rem' }} aria-hidden />
        <div
          style={{
            paddingBottom: '2rem',
            textAlign: 'center',
            fontSize: '0.875rem',
            color: '#6b7280',
          }}
        >
          {isLoading && <span>Loading more...</span>}
          {!isLoading && isEnd && questions.length > 0 && (
            <span>That's all for now</span>
          )}
        </div>
      </section>

      {/* Scroll to Top Button */}
      {showScrollToTop && (
        <button
          onClick={scrollToTop}
          style={{
            position: 'fixed',
            bottom: '12dvh',
            right: '1rem',
            zIndex: 1000,
            width: '3rem',
            height: '3rem',
            borderRadius: '50%',
            backgroundColor: 'grey',
            opacity: 0.65,
            border: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
          }}
        >
          <ChevronUp
            size={24}
            color="white"
            style={{
              strokeWidth: 2.5,
              fontFamily: 'Quicksand, sans-serif',
            }}
          />
        </button>
      )}
    </div>
  );
}
